import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, Asset } from '../lib/api';
import {
  ArrowLeft, MapPin, Calendar, User, Clock, Tag,
  AlertTriangle, Wrench, CheckCircle, XCircle,
  ChevronRight, RefreshCw, FileText, X, Settings,
  Truck, ArrowRightLeft, History, UserPlus, Flag, Plus,
  Package, MessageSquare, Sparkles, Send, Timer,
  ArrowUpRight, ChevronDown, Zap, Edit3,
  Info, MoreVertical, Search, Building2
} from 'lucide-react';

const statusConfig: Record<string, { label: string; bg: string; text: string; icon: any; gradient: string; border: string }> = {
  open: { label: 'Aperto', bg: 'bg-amber-50', text: 'text-amber-700', icon: AlertTriangle, gradient: 'from-amber-500 to-orange-500', border: 'border-amber-200' },
  in_progress: { label: 'In Lavorazione', bg: 'bg-blue-50', text: 'text-blue-700', icon: Wrench, gradient: 'from-blue-500 to-indigo-500', border: 'border-blue-200' },
  waiting: { label: 'In Attesa', bg: 'bg-orange-50', text: 'text-orange-700', icon: Timer, gradient: 'from-orange-500 to-red-500', border: 'border-orange-200' },
  resolved: { label: 'Risolto', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle, gradient: 'from-emerald-500 to-green-500', border: 'border-emerald-200' },
  closed: { label: 'Chiuso', bg: 'bg-gray-50', text: 'text-gray-600', icon: XCircle, gradient: 'from-gray-400 to-gray-500', border: 'border-gray-200' },
  cancelled: { label: 'Annullato', bg: 'bg-red-50', text: 'text-red-700', icon: XCircle, gradient: 'from-red-500 to-rose-500', border: 'border-red-200' }
};

const priorityConfig: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  low: { label: 'Bassa', icon: '🟢', color: 'text-green-600', bg: 'bg-green-100' },
  medium: { label: 'Media', icon: '🟡', color: 'text-yellow-600', bg: 'bg-yellow-100' },
  high: { label: 'Alta', icon: '🟠', color: 'text-orange-600', bg: 'bg-orange-100' },
  urgent: { label: 'Urgente', icon: '🔴', color: 'text-red-600', bg: 'bg-red-100' }
};

const typeConfig: Record<string, string> = {
  repair: 'Riparazione',
  maintenance: 'Manutenzione',
  inspection: 'Ispezione',
  replacement: 'Sostituzione',
  other: 'Altro'
};

const statusFlow: Record<string, string[]> = {
  open: ['in_progress'],
  in_progress: ['waiting', 'resolved', 'open'],
  waiting: ['in_progress', 'resolved'],
  resolved: ['closed', 'open'],
  closed: ['open'],
  cancelled: ['open']
};

const eventTypeConfig: Record<string, { label: string; icon: any; color: string; bgColor: string }> = {
  ticket_created: { label: 'Ticket Creato', icon: Plus, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  ticket_status_changed: { label: 'Stato cambiato', icon: RefreshCw, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  ticket_assigned: { label: 'Assegnazione', icon: UserPlus, color: 'text-purple-600', bgColor: 'bg-purple-100' },
  ticket_priority_changed: { label: 'Priorità cambiata', icon: Flag, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  location_change: { label: 'Spostamento', icon: Truck, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  status_change: { label: 'Stato Asset', icon: ArrowRightLeft, color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
  condition_change: { label: 'Condizione', icon: Settings, color: 'text-amber-600', bgColor: 'bg-amber-100' },
  maintenance: { label: 'Manutenzione', icon: Settings, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  repair: { label: 'Riparazione', icon: Wrench, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  replacement: { label: 'Sostituzione', icon: RefreshCw, color: 'text-red-600', bgColor: 'bg-red-100' },
  note: { label: 'Nota', icon: MessageSquare, color: 'text-gray-600', bgColor: 'bg-gray-100' },
  ticket_closed: { label: 'Ticket Chiuso', icon: CheckCircle, color: 'text-emerald-600', bgColor: 'bg-emerald-100' }
};

const assetStatusOptions = [
  { value: 'active', label: 'Attivo' },
  { value: 'in_repair', label: 'In Riparazione' },
  { value: 'in_maintenance', label: 'In Manutenzione' },
  { value: 'in_transit', label: 'In Transito' },
  { value: 'in_storage', label: 'In Magazzino' },
  { value: 'disposed', label: 'Dismesso' },
  { value: 'lost', label: 'Perso' }
];

type InterventionType = 'repair' | 'maintenance' | 'location_change' | 'replacement' | 'status_change' | 'note' | null;

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeForm, setActiveForm] = useState<InterventionType>(null);
  const [editingCostEventId, setEditingCostEventId] = useState<string | null>(null);
  const [completingEventId, setCompletingEventId] = useState<string | null>(null);
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.getTicket(id!),
    refetchOnMount: 'always'
  });

  const updateMutation = useMutation({
    mutationFn: (updateData: any) => api.updateTicket(id!, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Caricamento ticket...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 text-red-700 p-6 rounded-2xl border border-red-100">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Errore: {error?.message || 'Ticket non trovato'}</span>
        </div>
      </div>
    );
  }

  const { ticket, events, technicians, locations, availableAssets } = data;
  const currentStatus = statusConfig[ticket.status] || statusConfig.open;
  const currentPriority = priorityConfig[ticket.priority] || priorityConfig.medium;
  const nextStatuses = statusFlow[ticket.status] || [];
  const StatusIcon = currentStatus.icon;

  // Check for incomplete intervention (repair, maintenance, location_change - the ones that support isCompleted flow)
  const incompleteIntervention = events.find((e: any) => 
    (e.eventType === 'repair' || e.eventType === 'maintenance' || e.eventType === 'location_change') && e.isCompleted === false
  );
  const hasIncompleteIntervention = !!incompleteIntervention;

  // Types that require completing any ongoing intervention first
  const interventionTypesRequiringComplete = ['repair', 'maintenance', 'location_change', 'replacement', 'status_change'];

  // Handler for intervention button clicks
  const handleInterventionClick = (type: InterventionType) => {
    if (hasIncompleteIntervention && type && interventionTypesRequiringComplete.includes(type)) {
      setShowIncompleteWarning(true);
      return;
    }
    setActiveForm(activeForm === type ? null : type);
  };

  const handleStatusChange = (newStatus: string) => {
    updateMutation.mutate({ action: 'updateStatus', status: newStatus });
  };

  const handleAssign = (assignedTo: string) => {
    updateMutation.mutate({ action: 'assign', assignedTo: assignedTo || null });
  };

  const handlePriorityChange = (priority: string) => {
    updateMutation.mutate({ action: 'updatePriority', priority });
  };

  const formatDate = (date: string | number) => {
    return new Date(date).toLocaleString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (date: string | number) => {
    return new Date(date).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTimelineDate = (date: string | number): string => {
    return new Date(date).toLocaleString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (date: string | number): string => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return formatDate(date);
  };

  const getStatusActionLabel = (status: string): string => {
    switch (status) {
      case 'in_progress': return 'Prendi in carico';
      case 'waiting': return 'In Attesa';
      case 'resolved': return 'Segna come Risolto';
      case 'open': return 'Riapri';
      case 'closed': return 'Chiudi Ticket';
      default: return statusConfig[status]?.label || status;
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4 lg:space-y-6 animate-fadeIn pb-6">
      {/* Header Card - Enterprise Style */}
      <div className="card p-0 overflow-hidden">
        {/* Status Banner */}
        <div className={`${currentStatus.bg} ${currentStatus.border} border-b px-4 py-3 lg:px-6 lg:py-4`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-start lg:items-center gap-3">
              <Link to="/tickets" className="icon-btn bg-white/80 hover:bg-white flex-shrink-0">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-white/80 ${currentStatus.text}`}>
                  <StatusIcon className="w-4 h-4" />
                  {currentStatus.label}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium ${currentPriority.bg} ${currentPriority.color}`}>
                  {currentPriority.icon} {currentPriority.label}
                </span>
                {ticket.priority === 'urgent' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded-lg text-xs font-bold animate-pulse">
                    <AlertTriangle className="w-3 h-3" />
                    URGENTE
                  </span>
                )}
              </div>
            </div>
            
            {/* Quick Status Actions - Desktop */}
            <div className="hidden lg:flex items-center gap-2">
              {nextStatuses.map((status) => {
                const config = statusConfig[status];
                const Icon = config.icon;
                return (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    disabled={updateMutation.isPending}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white hover:bg-gray-50 border ${config.border} ${config.text} transition-all disabled:opacity-50`}
                  >
                    <Icon className="w-4 h-4" />
                    {getStatusActionLabel(status)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Title & Info */}
        <div className="p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-3">
            {ticket.ticketNumber && (
              <span className="text-lg lg:text-xl font-mono font-bold text-primary-600 bg-primary-50 px-3 py-1 rounded-lg">
                #{ticket.ticketNumber}
              </span>
            )}
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{ticket.title}</h1>
          </div>
          
          {/* Meta Info Row */}
          <div className="flex flex-wrap items-center gap-3 lg:gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span>{formatDateShort(ticket.createdAt)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Tag className="w-4 h-4 text-gray-400" />
              <span>{typeConfig[ticket.type] || ticket.type}</span>
            </div>
            {ticket.createdByUser && (
              <div className="flex items-center gap-1.5">
                <User className="w-4 h-4 text-gray-400" />
                <span>{ticket.createdByUser.firstName} {ticket.createdByUser.lastName}</span>
              </div>
            )}
            {ticket.assignedToUser && (
              <div className="flex items-center gap-1.5">
                <UserPlus className="w-4 h-4 text-gray-400" />
                <span>Assegnato a: <strong>{ticket.assignedToUser.firstName}</strong></span>
              </div>
            )}
          </div>
          
          {/* Description */}
          {ticket.description && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Status Actions */}
      <div className="lg:hidden">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {nextStatuses.map((status) => {
            const config = statusConfig[status];
            const Icon = config.icon;
            return (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                disabled={updateMutation.isPending}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium ${config.bg} ${config.text} border ${config.border} whitespace-nowrap disabled:opacity-50`}
              >
                <Icon className="w-4 h-4" />
                {getStatusActionLabel(status)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4 lg:space-y-6">
          {/* Linked Resources - Compact Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Asset Card */}
            {ticket.asset ? (
              <Link
                to={`/assets/${ticket.asset.id}`}
                className="card card-hover group p-4 flex items-center gap-3"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">Asset</p>
                  <p className="font-semibold text-gray-900 group-hover:text-primary-700 transition-colors truncate">{ticket.asset.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{ticket.asset.qrCode}</p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-gray-300 group-hover:text-primary-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
              </Link>
            ) : (
              <LinkAssetCard 
                ticketId={id!} 
                availableAssets={availableAssets || []} 
                onSuccess={() => queryClient.invalidateQueries({ queryKey: ['ticket', id] })}
              />
            )}

            {/* Location Card */}
            {(ticket.location || ticket.asset?.location) ? (
              <Link
                to={`/locations/${(ticket.location || ticket.asset?.location)?.id}`}
                className="card card-hover group p-4 flex items-center gap-3"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                  <MapPin className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">Ubicazione</p>
                  <p className="font-semibold text-gray-900 group-hover:text-primary-700 transition-colors truncate">{(ticket.location || ticket.asset?.location)?.name}</p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-gray-300 group-hover:text-primary-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
              </Link>
            ) : !ticket.asset && (
              <LinkLocationCard 
                ticketId={id!} 
                locations={locations} 
                onSuccess={() => queryClient.invalidateQueries({ queryKey: ['ticket', id] })}
              />
            )}
          </div>

          {/* Intervention Buttons */}
          <div className="card">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-primary-500" />
              Registra Intervento
            </h2>

            {/* Warning for incomplete intervention */}
            {hasIncompleteIntervention && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Timer className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800 text-sm">Intervento in corso</p>
                    <p className="text-xs text-amber-700 mt-1">
                      C'è un intervento di {incompleteIntervention.eventType === 'repair' ? 'riparazione' : incompleteIntervention.eventType === 'maintenance' ? 'manutenzione' : 'spostamento'} non completato.
                      Completalo prima di registrarne uno nuovo.
                    </p>
                    <button
                      onClick={() => setCompletingEventId(incompleteIntervention.id)}
                      className="inline-flex items-center gap-1 text-xs text-amber-800 hover:text-amber-900 font-semibold mt-2 bg-amber-100 px-2 py-1 rounded"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Completa intervento
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Warning modal for trying to create intervention while one is incomplete */}
            {showIncompleteWarning && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900">Intervento in corso</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Prima di registrare un nuovo intervento, devi completare l'intervento di{' '}
                    <strong>{incompleteIntervention?.eventType === 'repair' ? 'riparazione' : incompleteIntervention?.eventType === 'maintenance' ? 'manutenzione' : 'spostamento'}</strong> attualmente in corso.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowIncompleteWarning(false)}
                      className="flex-1 btn btn-secondary"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={() => {
                        setShowIncompleteWarning(false);
                        if (incompleteIntervention) {
                          setCompletingEventId(incompleteIntervention.id);
                        }
                      }}
                      className="flex-1 btn btn-primary"
                    >
                      Completa intervento
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              <button
                onClick={() => handleInterventionClick('repair')}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all ${
                  activeForm === 'repair' 
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25' 
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                Riparazione
              </button>
              <button
                onClick={() => handleInterventionClick('maintenance')}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all ${
                  activeForm === 'maintenance' 
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                Manutenzione
              </button>
              <button
                onClick={() => handleInterventionClick('location_change')}
                disabled={!ticket.assetId}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  activeForm === 'location_change' 
                    ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/25' 
                    : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                }`}
              >
                Spostamento
              </button>
              <button
                onClick={() => handleInterventionClick('replacement')}
                disabled={!ticket.assetId}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  activeForm === 'replacement' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/25' 
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                Sostituzione
              </button>
              <button
                onClick={() => handleInterventionClick('status_change')}
                disabled={!ticket.assetId}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  activeForm === 'status_change' 
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/25' 
                    : 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
                }`}
              >
                Cambio Stato
              </button>
              <button
                onClick={() => handleInterventionClick('note')}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all ${
                  activeForm === 'note' 
                    ? 'bg-gray-500 text-white shadow-lg shadow-gray-500/25' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Nota
              </button>
            </div>

            {/* Intervention Forms */}
            {activeForm && (
              <div className="mt-4 pt-4 border-t border-gray-100 animate-slideInUp">
                {activeForm === 'repair' && (
                  <RepairMaintenanceForm
                    type="repair"
                    ticketId={id!}
                    onSuccess={() => {
                      setActiveForm(null);
                      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
                    }}
                    onCancel={() => setActiveForm(null)}
                  />
                )}
                {activeForm === 'maintenance' && (
                  <RepairMaintenanceForm
                    type="maintenance"
                    ticketId={id!}
                    onSuccess={() => {
                      setActiveForm(null);
                      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
                    }}
                    onCancel={() => setActiveForm(null)}
                  />
                )}
                {activeForm === 'location_change' && (
                  <LocationChangeForm
                    ticketId={id!}
                    locations={locations}
                    currentLocation={ticket.asset?.location?.name}
                    onSuccess={() => {
                      setActiveForm(null);
                      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
                    }}
                    onCancel={() => setActiveForm(null)}
                  />
                )}
                {activeForm === 'replacement' && (
                  <ReplacementForm
                    ticketId={id!}
                    currentAsset={ticket.asset}
                    availableAssets={availableAssets || []}
                    onSuccess={() => {
                      setActiveForm(null);
                      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
                    }}
                    onCancel={() => setActiveForm(null)}
                  />
                )}
                {activeForm === 'status_change' && (
                  <StatusChangeForm
                    ticketId={id!}
                    currentStatus={ticket.asset?.status}
                    onSuccess={() => {
                      setActiveForm(null);
                      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
                    }}
                    onCancel={() => setActiveForm(null)}
                  />
                )}
                {activeForm === 'note' && (
                  <NoteForm
                    ticketId={id!}
                    onSuccess={() => {
                      setActiveForm(null);
                      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
                    }}
                    onCancel={() => setActiveForm(null)}
                  />
                )}
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="card">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
              <History className="w-4 h-4 text-indigo-500" />
              Timeline
              {events.length > 0 && <span className="text-xs font-normal text-gray-400">({events.length})</span>}
            </h2>

            {events.length === 0 ? (
              <div className="empty-state py-8">
                <History className="empty-state-icon" />
                <p className="empty-state-title">Nessuna attività</p>
                <p className="empty-state-text">Gli eventi del ticket appariranno qui</p>
              </div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto pr-2 pt-1 pl-0.5">
                <div className="space-y-3">
                  {events.map((event, index) => {
                    const config = eventTypeConfig[event.eventType] || eventTypeConfig.note;
                    const EventIcon = config.icon;
                    const isFirst = index === 0;
                    const supportsCompletion = event.eventType === 'repair' || event.eventType === 'maintenance' || event.eventType === 'location_change';
                    const hasCost = event.eventType === 'repair' || event.eventType === 'maintenance';
                    
                    return (
                      <div 
                        key={event.id} 
                        className="flex gap-3 animate-slideInUp"
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        {/* Timeline dot and line */}
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className={`w-9 h-9 rounded-xl ${config.bgColor} flex items-center justify-center`}>
                            <EventIcon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          {index < events.length - 1 && (
                            <div className="w-0.5 flex-1 bg-gray-200 mt-2 min-h-[20px]"></div>
                          )}
                        </div>
                        
                        {/* Event content */}
                        <div className="flex-1 pb-3 min-w-0">
                          <div className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <span className={`font-semibold text-sm ${config.color}`}>
                                    {config.label}
                                  </span>
                                  {supportsCompletion && event.isCompleted && (
                                    <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                                      <CheckCircle className="w-3 h-3" />
                                      Completato
                                    </span>
                                  )}
                                  {supportsCompletion && event.isCompleted === false && (
                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                                      In corso
                                    </span>
                                  )}
                                </div>
                              
                                {/* Show priority change */}
                                {event.eventType === 'ticket_priority_changed' && event.fromValue && event.toValue && (
                                  <p className="text-sm text-gray-600 mb-1.5">
                                    {priorityConfig[event.fromValue]?.icon} {priorityConfig[event.fromValue]?.label} → {priorityConfig[event.toValue]?.icon}{' '}
                                    <span className={priorityConfig[event.toValue]?.color}>{priorityConfig[event.toValue]?.label}</span>
                                  </p>
                                )}
                              
                                {/* Show location change */}
                                {event.eventType === 'location_change' && event.fromLocation && event.toLocation && (
                                  <p className="text-sm text-gray-600 mb-1.5 flex items-center gap-1">
                                    <MapPin className="w-3.5 h-3.5" />
                                    {event.fromLocation.name} → {event.toLocation.name}
                                  </p>
                                )}

                                {/* Show status change */}
                                {event.eventType === 'ticket_status_changed' && (
                                  <p className="text-sm text-gray-600 mb-1.5">
                                    → <span className={statusConfig[event.toStatus || '']?.text}>{statusConfig[event.toStatus || '']?.label}</span>
                                  </p>
                                )}

                                {/* Description */}
                                {event.description && (
                                  <p className="text-gray-700 text-sm bg-white rounded-lg p-2 mt-1.5 border border-gray-100">{event.description}</p>
                                )}
                              
                                {/* Cost and labor row */}
                                {(hasCost || event.laborMinutes) && (
                                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                                    {hasCost && (
                                      <div className="flex items-center gap-1.5">
                                        {editingCostEventId === event.id ? (
                                          <EditCostForm
                                            ticketId={id!}
                                            eventId={event.id}
                                            currentCost={event.cost}
                                            onSuccess={() => {
                                              setEditingCostEventId(null);
                                              queryClient.invalidateQueries({ queryKey: ['ticket', id] });
                                            }}
                                            onCancel={() => setEditingCostEventId(null)}
                                          />
                                        ) : (
                                          <>
                                            <span className="text-gray-400 text-xs">€</span>
                                            {event.cost !== null && event.cost !== undefined ? (
                                              <span className="font-medium text-gray-700">
                                                {(event.cost / 100).toFixed(2)}
                                              </span>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                            <button
                                              onClick={() => setEditingCostEventId(event.id)}
                                              className="text-xs text-primary-600 hover:text-primary-700 font-medium ml-1"
                                            >
                                              {event.cost ? 'Modifica' : 'Aggiungi'}
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                    {event.laborMinutes && (
                                      <div className="flex items-center gap-1.5 text-gray-600">
                                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                                        <span>{event.laborMinutes} min</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Complete intervention form for incomplete interventions */}
                                {!event.isCompleted && supportsCompletion && completingEventId === event.id && (
                                  <div className="mt-3 pt-3 border-t border-gray-100">
                                    <CompleteInterventionForm
                                      ticketId={id!}
                                      eventId={event.id}
                                      eventType={event.eventType}
                                      onSuccess={() => {
                                        setCompletingEventId(null);
                                        queryClient.invalidateQueries({ queryKey: ['ticket', id] });
                                      }}
                                      onCancel={() => setCompletingEventId(null)}
                                    />
                                  </div>
                                )}
                              
                                {/* User */}
                                {event.user && (
                                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {event.user.firstName} {event.user.lastName}
                                  </p>
                                )}
                              </div>
                              
                              {/* Right column: Date and Complete button */}
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <time className="text-xs text-gray-400">
                                  {formatTimelineDate(event.createdAt)}
                                </time>
                                {supportsCompletion && event.isCompleted === false && completingEventId !== event.id && (
                                  <button
                                    onClick={() => setCompletingEventId(event.id)}
                                    className="btn btn-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-700 border-emerald-200 flex items-center gap-1 mt-1"
                                  >
                                    <CheckCircle className="w-3 h-3" />
                                    Completa
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Assignment Card */}
          <div className="card">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-purple-500" />
              Assegnazione
            </h2>
            
            {ticket.assignedToUser ? (
              <div className="p-3 bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-xl border border-primary-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-semibold text-sm shadow-lg shadow-primary-500/25">
                    {ticket.assignedToUser.firstName[0]}{ticket.assignedToUser.lastName[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {ticket.assignedToUser.firstName} {ticket.assignedToUser.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{ticket.assignedToUser.email}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-center">
                <p className="text-sm text-gray-500">Non assegnato</p>
              </div>
            )}
            
            <select
              value={ticket.assignedTo || ''}
              onChange={(e) => handleAssign(e.target.value)}
              className="input mt-3"
              disabled={updateMutation.isPending}
            >
              <option value="">-- Seleziona tecnico --</option>
              {technicians.map((tech) => (
                <option key={tech.id} value={tech.id}>
                  {tech.firstName} {tech.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Details Card */}
          <div className="card">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Info className="w-4 h-4 text-gray-400" />
              Dettagli
            </h2>
            
            <dl className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <dt className="text-sm text-gray-500">Tipo</dt>
                <dd className="text-sm font-medium text-gray-900">{typeConfig[ticket.type] || ticket.type}</dd>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <dt className="text-sm text-gray-500">Priorità</dt>
                <dd>
                  <select
                    value={ticket.priority}
                    onChange={(e) => handlePriorityChange(e.target.value)}
                    className="input input-sm text-sm"
                    disabled={updateMutation.isPending}
                  >
                    {Object.entries(priorityConfig).map(([value, config]) => (
                      <option key={value} value={value}>
                        {config.icon} {config.label}
                      </option>
                    ))}
                  </select>
                </dd>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <dt className="text-sm text-gray-500">Creato</dt>
                <dd className="text-sm text-gray-900">{formatDateShort(ticket.createdAt)}</dd>
              </div>

              {ticket.resolvedAt && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <dt className="text-sm text-gray-500">Risolto</dt>
                  <dd className="text-sm text-emerald-600 font-medium">{formatDateShort(ticket.resolvedAt)}</dd>
                </div>
              )}
              
              {ticket.closedAt && (
                <div className="flex items-center justify-between py-2">
                  <dt className="text-sm text-gray-500">Chiuso</dt>
                  <dd className="text-sm text-gray-600">{formatDateShort(ticket.closedAt)}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Creator Card */}
          <div className="card">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              Creato da
            </h2>
            {ticket.createdByUser && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center text-white font-semibold text-sm">
                  {ticket.createdByUser.firstName[0]}{ticket.createdByUser.lastName[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {ticket.createdByUser.firstName} {ticket.createdByUser.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{formatDate(ticket.createdAt)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FORM COMPONENTS
// ============================================================================

function RepairMaintenanceForm({ 
  type, 
  ticketId, 
  onSuccess, 
  onCancel 
}: { 
  type: 'repair' | 'maintenance';
  ticketId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState('');
  const [isCompleted, setIsCompleted] = useState(true);
  const [cost, setCost] = useState('');
  const [laborMinutes, setLaborMinutes] = useState('');

  const mutation = useMutation({
    mutationFn: (data: any) => api.createTicketEvent(ticketId, data),
    onSuccess
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      eventType: type,
      description: description || undefined,
      isCompleted,
      cost: cost ? Math.round(parseFloat(cost) * 100) : undefined,
      laborMinutes: laborMinutes ? parseInt(laborMinutes) : undefined
    });
  };

  const label = type === 'repair' ? 'Riparazione' : 'Manutenzione';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
        <Wrench className="w-5 h-5" />
        {label}
        <button type="button" onClick={onCancel} className="ml-auto text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </h3>

      <div className="form-group">
        <label className="form-label">Descrizione intervento</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input"
          rows={3}
          placeholder={`Descrivi l'intervento di ${type === 'repair' ? 'riparazione' : 'manutenzione'}...`}
        />
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={isCompleted}
          onChange={(e) => setIsCompleted(e.target.checked)}
          className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <div>
          <span className="font-medium text-gray-900">{label} completata</span>
          <p className="text-sm text-gray-500">L'asset resta nello stato attuale</p>
        </div>
      </label>

      <div className="grid grid-cols-2 gap-4">
        <div className="form-group">
          <label className="form-label">Costo materiali (€)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            className="input"
            placeholder="0.00"
          />
          <p className="text-xs text-gray-500 mt-1">Puoi aggiungerlo anche dopo</p>
        </div>
        <div className="form-group">
          <label className="form-label">Tempo lavoro (min)</label>
          <input
            type="number"
            min="0"
            value={laborMinutes}
            onChange={(e) => setLaborMinutes(e.target.value)}
            className="input"
            placeholder="0"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary flex-1">
          Annulla
        </button>
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">
          {mutation.isPending ? 'Salvataggio...' : 'Registra'}
        </button>
      </div>
    </form>
  );
}

function LocationChangeForm({ 
  ticketId, 
  locations,
  currentLocation,
  onSuccess, 
  onCancel 
}: { 
  ticketId: string;
  locations: any[];
  currentLocation?: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [isCompleted, setIsCompleted] = useState(true);

  const mutation = useMutation({
    mutationFn: (data: any) => api.createTicketEvent(ticketId, data),
    onSuccess
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!toLocationId) return;
    mutation.mutate({
      eventType: 'location_change',
      description: description || undefined,
      toLocationId,
      isCompleted
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
        <Truck className="w-5 h-5" />
        Spostamento
        <button type="button" onClick={onCancel} className="ml-auto text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </h3>

      <div className="form-group">
        <label className="form-label">Descrizione intervento</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input"
          rows={2}
          placeholder="Motivo dello spostamento..."
        />
      </div>

      <div className="form-group">
        <label className="form-label">Nuova ubicazione <span className="text-red-500">*</span></label>
        <select
          value={toLocationId}
          onChange={(e) => setToLocationId(e.target.value)}
          className="input"
          required
        >
          <option value="">Seleziona ubicazione...</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>{loc.name}</option>
          ))}
        </select>
        {currentLocation && (
          <p className="text-xs text-gray-500 mt-1">Ubicazione attuale: {currentLocation}</p>
        )}
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={isCompleted}
          onChange={(e) => setIsCompleted(e.target.checked)}
          className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <div>
          <span className="font-medium text-gray-900">Spostamento completato</span>
          <p className="text-sm text-gray-500">
            {isCompleted 
              ? "L'asset è già nella nuova posizione" 
              : "L'asset verrà messo in transito"}
          </p>
        </div>
      </label>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary flex-1">
          Annulla
        </button>
        <button type="submit" disabled={mutation.isPending || !toLocationId} className="btn btn-primary flex-1">
          {mutation.isPending ? 'Salvataggio...' : 'Registra'}
        </button>
      </div>
    </form>
  );
}

function ReplacementForm({ 
  ticketId, 
  currentAsset,
  availableAssets,
  onSuccess, 
  onCancel 
}: { 
  ticketId: string;
  currentAsset: any;
  availableAssets: Asset[];
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState('');
  const [replacementAssetId, setReplacementAssetId] = useState('');

  const mutation = useMutation({
    mutationFn: (data: any) => api.createTicketEvent(ticketId, data),
    onSuccess
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replacementAssetId) return;
    mutation.mutate({
      eventType: 'replacement',
      description: description || undefined,
      replacementAssetId
    });
  };

  // Filter out current asset from available assets
  const filteredAssets = availableAssets.filter(a => a.id !== currentAsset?.id);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
        <RefreshCw className="w-5 h-5" />
        Sostituzione
        <button type="button" onClick={onCancel} className="ml-auto text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </h3>

      <div className="form-group">
        <label className="form-label">Descrizione intervento</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input"
          rows={2}
          placeholder="Motivo della sostituzione..."
        />
      </div>

      <div className="form-group">
        <label className="form-label">Asset sostitutivo <span className="text-red-500">*</span></label>
        <select
          value={replacementAssetId}
          onChange={(e) => setReplacementAssetId(e.target.value)}
          className="input"
          required
        >
          <option value="">Seleziona asset...</option>
          {filteredAssets.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.name} - {asset.location?.name || 'Senza ubicazione'}
            </option>
          ))}
        </select>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
        <p className="font-medium text-amber-800 mb-2">Cosa succederà:</p>
        <ul className="space-y-1 text-amber-700">
          <li>• L'asset corrente ({currentAsset?.name}) verrà messo in magazzino</li>
          <li>• L'asset selezionato verrà spostato nella posizione dell'asset corrente</li>
          <li>• Verrà registrato uno spostamento per sostituzione sull'asset sostituto</li>
          <li>• Il ticket continuerà a riferirsi all'asset originale per tracciabilità</li>
        </ul>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary flex-1">
          Annulla
        </button>
        <button type="submit" disabled={mutation.isPending || !replacementAssetId} className="btn btn-primary flex-1">
          {mutation.isPending ? 'Salvataggio...' : 'Registra'}
        </button>
      </div>
    </form>
  );
}

function StatusChangeForm({ 
  ticketId, 
  currentStatus,
  onSuccess, 
  onCancel 
}: { 
  ticketId: string;
  currentStatus?: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState('');
  const [toStatus, setToStatus] = useState('');

  const mutation = useMutation({
    mutationFn: (data: any) => api.createTicketEvent(ticketId, data),
    onSuccess
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!toStatus) return;
    mutation.mutate({
      eventType: 'status_change',
      description: description || undefined,
      toStatus
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
        <ArrowRightLeft className="w-5 h-5" />
        Cambio Stato Asset
        <button type="button" onClick={onCancel} className="ml-auto text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </h3>

      <div className="form-group">
        <label className="form-label">Descrizione intervento</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input"
          rows={2}
          placeholder="Motivo del cambio stato..."
        />
      </div>

      <div className="form-group">
        <label className="form-label">Nuovo stato <span className="text-red-500">*</span></label>
        <select
          value={toStatus}
          onChange={(e) => setToStatus(e.target.value)}
          className="input"
          required
        >
          <option value="">Seleziona stato...</option>
          {assetStatusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {currentStatus && (
          <p className="text-xs text-gray-500 mt-1">
            Stato attuale: {assetStatusOptions.find(s => s.value === currentStatus)?.label || currentStatus}
          </p>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary flex-1">
          Annulla
        </button>
        <button type="submit" disabled={mutation.isPending || !toStatus} className="btn btn-primary flex-1">
          {mutation.isPending ? 'Salvataggio...' : 'Registra'}
        </button>
      </div>
    </form>
  );
}

function NoteForm({ 
  ticketId, 
  onSuccess, 
  onCancel 
}: { 
  ticketId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState('');

  const mutation = useMutation({
    mutationFn: (data: any) => api.createTicketEvent(ticketId, data),
    onSuccess
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;
    mutation.mutate({
      eventType: 'note',
      description
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
        <MessageSquare className="w-5 h-5" />
        Nota
        <button type="button" onClick={onCancel} className="ml-auto text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </h3>

      <div className="form-group">
        <label className="form-label">Descrizione intervento</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input"
          rows={3}
          placeholder="Scrivi una nota o commento..."
          required
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary flex-1">
          Annulla
        </button>
        <button type="submit" disabled={mutation.isPending || !description.trim()} className="btn btn-primary flex-1">
          {mutation.isPending ? 'Salvataggio...' : 'Registra'}
        </button>
      </div>
    </form>
  );
}

function EditCostForm({ 
  ticketId,
  eventId,
  currentCost,
  onSuccess, 
  onCancel 
}: { 
  ticketId: string;
  eventId: string;
  currentCost: number | null;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [cost, setCost] = useState(currentCost ? (currentCost / 100).toFixed(2) : '');

  const mutation = useMutation({
    mutationFn: (data: any) => api.updateTicketEvent(ticketId, data),
    onSuccess
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      eventId,
      cost: cost ? Math.round(parseFloat(cost) * 100) : null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2">
      <input
        type="number"
        step="0.01"
        min="0"
        value={cost}
        onChange={(e) => setCost(e.target.value)}
        className="input py-1 px-2 w-24 text-sm"
        placeholder="0.00"
        autoFocus
      />
      <button type="submit" disabled={mutation.isPending} className="text-primary-600 hover:text-primary-700">
        <CheckCircle className="w-5 h-5" />
      </button>
      <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
        <X className="w-5 h-5" />
      </button>
    </form>
  );
}

const conditionOptions = [
  { value: 'excellent', label: 'Eccellente' },
  { value: 'good', label: 'Buono' },
  { value: 'fair', label: 'Discreto' },
  { value: 'poor', label: 'Scarso' },
  { value: 'broken', label: 'Rotto' }
];

function CompleteInterventionForm({ 
  ticketId,
  eventId,
  eventType,
  onSuccess, 
  onCancel 
}: { 
  ticketId: string;
  eventId: string;
  eventType: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [cost, setCost] = useState('');
  const [toCondition, setToCondition] = useState('');

  const mutation = useMutation({
    mutationFn: (data: any) => api.updateTicketEvent(ticketId, data),
    onSuccess
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      eventId,
      complete: true,
      cost: cost ? Math.round(parseFloat(cost) * 100) : undefined,
      toCondition: toCondition || undefined
    });
  };

  const label = eventType === 'repair' ? 'Riparazione' : eventType === 'maintenance' ? 'Manutenzione' : 'Spostamento';
  const isMove = eventType === 'location_change';

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          Completa {label}
        </h4>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      {!isMove && (
        <div className="grid grid-cols-2 gap-2">
          <div className="form-group">
            <label className="text-xs text-gray-500">Costo (€)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              className="input input-sm"
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label className="text-xs text-gray-500">Nuova condizione</label>
            <select
              value={toCondition}
              onChange={(e) => setToCondition(e.target.value)}
              className="input input-sm"
            >
              <option value="">Nessun cambio</option>
              {conditionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {isMove && (
        <p className="text-sm text-gray-600">
          Conferma che l'asset è arrivato nella nuova ubicazione.
        </p>
      )}

      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary btn-sm flex-1">
          Annulla
        </button>
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm flex-1">
          {mutation.isPending ? 'Salvataggio...' : 'Completa'}
        </button>
      </div>
    </form>
  );
}

function LinkAssetCard({ 
  ticketId, 
  availableAssets,
  onSuccess 
}: { 
  ticketId: string;
  availableAssets: Asset[];
  onSuccess: () => void;
}) {
  const [isLinking, setIsLinking] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const mutation = useMutation({
    mutationFn: (assetId: string) => api.updateTicket(ticketId, { action: 'linkAsset', assetId }),
    onSuccess: () => {
      setIsLinking(false);
      setSelectedAssetId('');
      setSearchTerm('');
      onSuccess();
    }
  });

  const filteredAssets = availableAssets.filter(a => 
    a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.qrCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.location?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedAsset = availableAssets.find(a => a.id === selectedAssetId);

  const handleLink = () => {
    if (selectedAssetId) {
      mutation.mutate(selectedAssetId);
    }
  };

  if (!isLinking) {
    return (
      <div 
        onClick={() => setIsLinking(true)}
        className="card card-hover group p-4 flex items-center gap-3 cursor-pointer border-2 border-dashed border-gray-200 hover:border-blue-300 bg-gradient-to-br from-gray-50/50 to-white"
      >
        <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          <Package className="w-6 h-6 text-blue-500" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">Asset</p>
          <p className="font-medium text-gray-500">Nessun asset collegato</p>
          <p className="text-xs text-blue-600 font-medium mt-1 flex items-center gap-1">
            <Plus className="w-3 h-3" />
            Clicca per collegare
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4 border-2 border-blue-200 bg-gradient-to-br from-blue-50/30 to-white">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Package className="w-4 h-4 text-blue-600" />
          </div>
          <h3 className="font-semibold text-gray-900">Collega Asset</h3>
        </div>
        <button 
          onClick={() => { setIsLinking(false); setSearchTerm(''); setSelectedAssetId(''); }}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Search Input */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Cerca per nome, codice QR o ubicazione..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input pl-10 text-sm"
          autoFocus
        />
      </div>

      {/* Asset List */}
      <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl bg-white mb-3">
        {filteredAssets.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            <Package className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            {searchTerm ? 'Nessun asset trovato' : 'Nessun asset disponibile'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                onClick={() => setSelectedAssetId(asset.id)}
                className={`p-3 cursor-pointer transition-all hover:bg-blue-50 ${
                  selectedAssetId === asset.id 
                    ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                    : 'border-l-4 border-l-transparent'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selectedAssetId === asset.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Package className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${
                      selectedAssetId === asset.id ? 'text-blue-700' : 'text-gray-900'
                    }`}>
                      {asset.name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {asset.qrCode && <span className="font-mono">{asset.qrCode}</span>}
                      {asset.location && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="w-3 h-3" />
                          {asset.location.name}
                        </span>
                      )}
                    </div>
                  </div>
                  {selectedAssetId === asset.id && (
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Asset Preview */}
      {selectedAsset && (
        <div className="p-3 bg-blue-50 rounded-xl border border-blue-200 mb-3">
          <p className="text-xs text-blue-600 font-medium mb-1">Asset selezionato</p>
          <p className="font-semibold text-blue-800">{selectedAsset.name}</p>
          {selectedAsset.location && (
            <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3" />
              {selectedAsset.location.name}
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button 
          onClick={() => { setIsLinking(false); setSearchTerm(''); setSelectedAssetId(''); }}
          className="btn btn-secondary flex-1 text-sm"
        >
          Annulla
        </button>
        <button 
          onClick={handleLink}
          disabled={!selectedAssetId || mutation.isPending}
          className="btn btn-primary flex-1 text-sm"
        >
          {mutation.isPending ? (
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Collegamento...
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              Collega Asset
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

function LinkLocationCard({ 
  ticketId, 
  locations,
  onSuccess 
}: { 
  ticketId: string;
  locations: any[];
  onSuccess: () => void;
}) {
  const [isLinking, setIsLinking] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const mutation = useMutation({
    mutationFn: (locationId: string) => api.updateTicket(ticketId, { action: 'linkLocation', locationId }),
    onSuccess: () => {
      setIsLinking(false);
      setSelectedLocationId('');
      setSearchTerm('');
      onSuccess();
    }
  });

  const filteredLocations = locations.filter(loc => 
    loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    loc.type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedLocation = locations.find(l => l.id === selectedLocationId);

  const handleLink = () => {
    if (selectedLocationId) {
      mutation.mutate(selectedLocationId);
    }
  };

  const getLocationIcon = (type?: string) => {
    switch (type) {
      case 'building': return Building2;
      case 'area': return MapPin;
      default: return MapPin;
    }
  };

  if (!isLinking) {
    return (
      <div 
        onClick={() => setIsLinking(true)}
        className="card card-hover group p-4 flex items-center gap-3 cursor-pointer border-2 border-dashed border-gray-200 hover:border-emerald-300 bg-gradient-to-br from-gray-50/50 to-white"
      >
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          <MapPin className="w-6 h-6 text-emerald-500" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">Ubicazione</p>
          <p className="font-medium text-gray-500">Nessuna ubicazione collegata</p>
          <p className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
            <Plus className="w-3 h-3" />
            Clicca per collegare
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/30 to-white">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
            <MapPin className="w-4 h-4 text-emerald-600" />
          </div>
          <h3 className="font-semibold text-gray-900">Collega Ubicazione</h3>
        </div>
        <button 
          onClick={() => { setIsLinking(false); setSearchTerm(''); setSelectedLocationId(''); }}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Search Input */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Cerca per nome o tipo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input pl-10 text-sm"
          autoFocus
        />
      </div>

      {/* Location List */}
      <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl bg-white mb-3">
        {filteredLocations.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            <MapPin className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            {searchTerm ? 'Nessuna ubicazione trovata' : 'Nessuna ubicazione disponibile'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredLocations.map((loc) => {
              const LocationIcon = getLocationIcon(loc.type);
              return (
                <div
                  key={loc.id}
                  onClick={() => setSelectedLocationId(loc.id)}
                  className={`p-3 cursor-pointer transition-all hover:bg-emerald-50 ${
                    selectedLocationId === loc.id 
                      ? 'bg-emerald-50 border-l-4 border-l-emerald-500' 
                      : 'border-l-4 border-l-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      selectedLocationId === loc.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <LocationIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${
                        selectedLocationId === loc.id ? 'text-emerald-700' : 'text-gray-900'
                      }`}>
                        {loc.name}
                      </p>
                      {loc.type && (
                        <p className="text-xs text-gray-500 capitalize">{loc.type}</p>
                      )}
                    </div>
                    {selectedLocationId === loc.id && (
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Location Preview */}
      {selectedLocation && (
        <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 mb-3">
          <p className="text-xs text-emerald-600 font-medium mb-1">Ubicazione selezionata</p>
          <p className="font-semibold text-emerald-800">{selectedLocation.name}</p>
          {selectedLocation.type && (
            <p className="text-xs text-emerald-600 capitalize mt-1">{selectedLocation.type}</p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button 
          onClick={() => { setIsLinking(false); setSearchTerm(''); setSelectedLocationId(''); }}
          className="btn btn-secondary flex-1 text-sm"
        >
          Annulla
        </button>
        <button 
          onClick={handleLink}
          disabled={!selectedLocationId || mutation.isPending}
          className="btn btn-primary flex-1 text-sm"
        >
          {mutation.isPending ? (
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Collegamento...
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              Collega Ubicazione
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
